/**
 * Asistentul de pronunție (spec-pipeline §5). Alegi un cuvânt dintr-un segment →
 * sugestii fonetice (respelling, ex. „ma-nele") → aplici → se regenerează doar segmentul.
 */
import { useState } from 'react';
import type { ScenarioSegment } from '@reclame-ai/shared';
import { suggestPronunciation, applyPronunciation } from '../api/endpoints';
import { useMutation, useInvalidate, qk } from '../api/hooks';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { Spinner } from './ui';
import { SearchSelect } from './SearchSelect';

export function PronunciationAssistant({
  clipId,
  segments,
  onApplied,
}: {
  clipId: string;
  segments: ScenarioSegment[];
  onApplied?: () => void;
}) {
  const [segmentIndex, setSegmentIndex] = useState(segments[0]?.index ?? 0);
  const [word, setWord] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const invalidate = useInvalidate();
  const toast = useToast();

  const suggestMut = useMutation({
    mutationFn: () =>
      suggestPronunciation(clipId, {
        word: word.trim(),
        segmentIndex,
        context: segments.find((s) => s.index === segmentIndex)?.text,
      }),
    onSuccess: (res) => {
      const list = (res.suggestions ?? []).map((s) => s.respelling);
      setSuggestions(list);
      setSelected(list[0] ?? null);
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut genera sugestii', body: humanError(err) }),
  });

  const applyMut = useMutation({
    mutationFn: () => applyPronunciation(clipId, { segmentIndex, respelling: selected as string }),
    onSuccess: () => {
      invalidate([qk.clip(clipId)]);
      toast.push({ kind: 'ok', title: 'Pronunție aplicată', body: 'Segmentul afectat se regenerează.' });
      setSuggestions([]);
      setSelected(null);
      setWord('');
      onApplied?.();
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut aplica', body: humanError(err) }),
  });

  return (
    <div className="card" data-testid="pronunciation-assistant" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <h3 className="bricolage" style={{ fontSize: 16, fontWeight: 800 }}>Corectează pronunția</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
          Scrie cuvântul care sună greșit — îți propunem variante fonetice (ex. „ma-nele").
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="label" style={{ flex: 1, minWidth: 160 }}>
          Cuvântul
          <input className="input" data-testid="pron-word" value={word} placeholder="ex. manele" onChange={(e) => setWord(e.target.value)} />
        </label>
        <label className="label" style={{ minWidth: 150 }}>
          Segmentul
          <SearchSelect
            value={String(segmentIndex)}
            onChange={(v) => setSegmentIndex(Number(v))}
            options={segments.map((s) => ({ value: String(s.index), label: `Scena ${s.index + 1} (${s.kind})` }))}
            aria-label="Segmentul"
            searchPlaceholder="Caută scenă…"
            data-testid="pron-segment"
          />
        </label>
        <button className="btn btn-ghost btn-sm" data-testid="pron-suggest" onClick={() => suggestMut.mutate()} disabled={!word.trim() || suggestMut.isPending}>
          {suggestMut.isPending ? <Spinner /> : 'Sugerează'}
        </button>
      </div>

      {suggestions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} data-testid="pron-suggestions">
            {suggestions.map((s) => (
              <button key={s} className="chip mono" data-on={selected === s} data-testid="pron-suggestion" onClick={() => setSelected(s)}>
                {s}
              </button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" data-testid="pron-apply" style={{ alignSelf: 'flex-start' }} onClick={() => applyMut.mutate()} disabled={!selected || applyMut.isPending}>
            {applyMut.isPending ? <Spinner /> : 'Aplică și regenerează segmentul'}
          </button>
        </div>
      )}
    </div>
  );
}
